/**
 * MobileClockInSidebar — Slide-out side menu for MobileClockIn.
 * Shows supervisor or operator navigation links and user info/logout.
 * Extracted from MobileClockIn.tsx for maintainability.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../i18n/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import {
  Home,
  LayoutGrid,
  ListChecks,
  BarChart3,
  Settings,
  Package,
  ListTodo,
  CalendarDays,
  HelpCircle,
  LogOut,
} from 'lucide-react';

interface MobileClockInSidebarProps {
  isSupervisor: boolean;
  onClose: () => void;
}

const MobileClockInSidebar: React.FC<MobileClockInSidebarProps> = ({
  isSupervisor,
  onClose,
}) => {
  const { t } = useLanguage();
  const { logout, user } = useAuth();

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* Menu Panel */}
      <div className="relative w-72 md:w-80 lg:w-96 max-w-[80%] bg-white dark:bg-gray-800 h-full shadow-xl flex flex-col">
        {/* Menu Header with Logo */}
        <div className={`px-4 py-5 border-b border-gray-200 dark:border-gray-700 ${isSupervisor ? 'bg-blue-600 dark:bg-blue-700' : ''}`}>
          <img
            src="/wf-removebg-preview.png"
            alt="Way Fong"
            className={`h-12 w-auto ${isSupervisor ? 'brightness-0 invert' : ''}`}
          />
          {isSupervisor && (
            <p className="text-purple-200 text-sm mt-1">{t('mobileSupervisor.title') || 'Supervisor Mode'}</p>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {isSupervisor ? (
            <>
              {/* Supervisor Navigation */}
              <Link
                to="/supervisor"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Home className="w-5 h-5 mr-3 text-purple-500" />
                {t('mobileSupervisor.title') || 'Supervisor Home'}
              </Link>
              <Link
                to="/supervisor/stations"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <LayoutGrid className="w-5 h-5 mr-3 text-blue-500" />
                {t('mobileSupervisor.stationOverview') || 'Station Overview'}
              </Link>
              <Link
                to="/supervisor/sessions"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ListChecks className="w-5 h-5 mr-3 text-green-500" />
                {t('mobileSupervisor.sessionManagement') || 'Sessions'}
              </Link>
              <Link
                to="/supervisor/summary"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <BarChart3 className="w-5 h-5 mr-3 text-indigo-500" />
                {t('mobileSupervisor.endOfDaySummary') || 'Daily Summary'}
              </Link>
              <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
              <Link
                to="/supervisor"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.desktopView') || 'Desktop View'}
              </Link>
            </>
          ) : (
            <>
              {/* Operator Navigation — matches Layout.tsx operatorNavigationItems */}
              <Link
                to="/schedule?tab=daily"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ListTodo className="w-5 h-5 mr-3 text-gray-400" />
                {t('schedule.dailyQueue') || 'Daily Queue'}
              </Link>
              <Link
                to="/schedule?tab=weekly"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <CalendarDays className="w-5 h-5 mr-3 text-gray-400" />
                {t('schedule.weeklyView') || 'Weekly View'}
              </Link>
              <Link
                to="/operator/production"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Package className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.production') || 'Production'}
              </Link>
              <Link
                to="/tutorial"
                onClick={onClose}
                className="flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <HelpCircle className="w-5 h-5 mr-3 text-gray-400" />
                {t('nav.tutorial') || 'Tutorial'}
              </Link>
            </>
          )}
        </nav>

        {/* User Info & Logout at Bottom */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 ${isSupervisor ? 'bg-purple-600' : 'bg-blue-600'} rounded-full flex items-center justify-center text-white font-bold`}>
              {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username || 'User'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{user?.role || (isSupervisor ? 'Supervisor' : 'Operator')}</p>
            </div>
          </div>
          <button
            onClick={() => {
              onClose();
              logout();
            }}
            className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {t('nav.logout') || 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileClockInSidebar;
